import { useState, useCallback } from "react";
import {
  searchContactsV2,
  ContactV2Request,
  ContactV2Response,
  LeadV2,
  PaginationMetadata,
} from "../services/leadsServiceV2";

interface UseLeadsV2Return {
  leads: LeadV2[];
  pagination: PaginationMetadata | null;
  isLoading: boolean;
  error: string | null;
  searchLeads: (request: ContactV2Request) => Promise<void>;
  loadNextPage: () => Promise<void>;
  loadPreviousPage: () => Promise<void>;
  resetSearch: () => void;
  canGoBack: boolean;
  canGoNext: boolean;
}

const useLeadsV2 = (): UseLeadsV2Return => {
  const [leads, setLeads] = useState<LeadV2[]>([]);
  const [pagination, setPagination] = useState<PaginationMetadata | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [lastRequest, setLastRequest] = useState<ContactV2Request | null>(null);
  
  // UI-side pagination token history for backward navigation
  const [pageTokenHistory, setPageTokenHistory] = useState<(string | null)[]>([]);
  const [currentPageIndex, setCurrentPageIndex] = useState<number>(0);

  // Search leads with v2 API
  const searchLeads = useCallback(async (request: ContactV2Request) => {
    setIsLoading(true);
    setError(null);

    try {
      const response: ContactV2Response = await searchContactsV2(request);
      setLeads(response.contacts);
      setPagination(response.pagination);
      setLastRequest(request);
      
      // Reset pagination history for new search
      if (!request.paginationToken) {
        setPageTokenHistory([null]); // First page has no token
        setCurrentPageIndex(0);
      }
    } catch (err: any) {
      setError(err.message);
      setLeads([]);
      setPagination(null);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Load next page
  const loadNextPage = useCallback(async () => {
    if (!lastRequest || !pagination?.hasNext || !pagination?.nextPageToken || isLoading) {
      return;
    }

    // Store current page token in history before moving forward
    const newHistory = [...pageTokenHistory];
    if (currentPageIndex === newHistory.length - 1) {
      // We're at the latest page, add next page token to history
      newHistory.push(pagination.nextPageToken);
      setPageTokenHistory(newHistory);
    }

    const nextPageRequest: ContactV2Request = {
      ...lastRequest,
      page: pagination.currentPage + 1,
      paginationToken: pagination.nextPageToken,
    };

    setCurrentPageIndex(currentPageIndex + 1);
    await searchLeads(nextPageRequest);
  }, [lastRequest, pagination, searchLeads, pageTokenHistory, currentPageIndex, isLoading]);

  // Load previous page using token history
  const loadPreviousPage = useCallback(async () => {
    if (!lastRequest || currentPageIndex <= 0 || isLoading) {
      return;
    }

    const previousPageIndex = currentPageIndex - 1;
    const previousPageToken = pageTokenHistory[previousPageIndex];

    const prevPageRequest: ContactV2Request = {
      ...lastRequest,
      page: previousPageIndex + 1, // Pages are 1-indexed
      paginationToken: previousPageToken || undefined,
    };

    setCurrentPageIndex(previousPageIndex);
    await searchLeads(prevPageRequest);
  }, [lastRequest, currentPageIndex, pageTokenHistory, searchLeads, isLoading]);

  // Reset search state and pagination history
  const resetSearch = useCallback(() => {
    setLeads([]);
    setPagination(null);
    setError(null);
    setLastRequest(null);
    setPageTokenHistory([]);
    setCurrentPageIndex(0);
  }, []);

  // Determine if navigation is possible
  const canGoBack = currentPageIndex > 0;
  const canGoNext = pagination?.hasNext ?? false;

  return {
    leads,
    pagination,
    isLoading,
    error,
    searchLeads,
    loadNextPage,
    loadPreviousPage,
    resetSearch,
    canGoBack,
    canGoNext,
  };
};

export default useLeadsV2;
